import React, { useState, useEffect, useRef } from "react";

import BcSepIcon from "../../asset/images/about/breadcrumb-separator.svg";

// Desktop + Mobile hero images — imported separately
import HeroBgImg       from "../../asset/images/products/esp-power-supplies/hero-bg.webp";
import HeroBgImgMobile from "../../asset/images/products/esp-power-supplies/hero-bg-mobile.webp";

// Slider images
import SlideImg1 from "../../asset/images/products/esp-power-supplies/slide-1.webp";
import SlideImg2 from "../../asset/images/products/esp-power-supplies/slide-2.webp";
import SlideImg3 from "../../asset/images/products/esp-power-supplies/slide-3.webp";
import SlideImg4 from "../../asset/images/products/esp-power-supplies/slide-4.webp";

// Product card background images
import PhaseOneImg   from "../../asset/images/products/esp-power-supplies/1-phase-card.webp";
import PhaseThreeImg from "../../asset/images/products/esp-power-supplies/3-phase-card.webp";


const SLIDES = [SlideImg1, SlideImg2, SlideImg3, SlideImg4];
const AUTOPLAY_SPEED = 10000;

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .epsp-wrap { font-family: 'Poppins', sans-serif; color: #202020; background: #fff; }

  /* ══════════════════════════════════════════
     1. HERO
  ══════════════════════════════════════════ */
  .epsp-hero {
    position: relative;
    width: 100%;
    height: 460px;
    overflow: hidden;
    background: #111;
  }
  .epsp-hero-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
  .epsp-hero-bg-mobile {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: none;
  }
  .epsp-hero-overlay { position: absolute; inset: 0; }
  .epsp-hero-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 102px;
    max-width: 1600px;
    margin: 0 auto;
    left: 0; right: 0;
  }
  .epsp-hero-title {
    font-family: 'Poppins', sans-serif;
    font-size: 42px;
    font-weight: 700;
    color: #fff;
    line-height: 120%;
    margin-bottom: 15px;
  }
  .epsp-hero-divider {
    width: 563px;
    height: 2px;
    background: #EB2019;
    border: none;
    margin-bottom: 18px;
  }
  .epsp-hero-desc {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #FFFFFF;
    line-height: 150%;
    max-width: 563px;
  }

  /* ══════════════════════════════════════════
     2. BREADCRUMB
  ══════════════════════════════════════════ */
  .epsp-breadcrumb { padding: 40px 0 0 0; }
  .epsp-breadcrumb-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .epsp-bc-item {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #202020;
    text-decoration: none;
    transition: color 0.15s;
  }
  .epsp-bc-item:hover { color: #EB2019; }
  .epsp-bc-item.epsp-active { color: #202020; }
  .epsp-bc-sep {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin: 0 8px;
    flex-shrink: 0;
  }

  /* ══════════════════════════════════════════
     SHARED INNER
  ══════════════════════════════════════════ */
  .epsp-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }

  /* ══════════════════════════════════════════
     3. CONTENT SECTION
  ══════════════════════════════════════════ */
  .epsp-section-content {
    padding: 80px 0 0 0;
  }

  /* Red page title */
  .epsp-red-title {
    font-family: 'Poppins', sans-serif;
    font-size: 32px;
    font-weight: 600;
    color: #EB2019;
    margin-bottom: 60px;
    line-height: 120%;
  }

  /* Two-col grid: left slider + right paragraphs */
  .epsp-top-grid {
    display: flex;
    gap: 90px;
    align-items: center;
    margin-bottom: 80px;
  }

  /* ── SLIDER ── */
  .epsp-slider {
    flex: 0 0 713px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .epsp-slider-img-wrap {
    width: 100%;
    height: 500px;
    border-radius: 10px;
    overflow: hidden;
    cursor: grab;
    user-select: none;
  }
  .epsp-slider-img-wrap:active { cursor: grabbing; }
  .epsp-slider-track {
    display: flex;
    height: 100%;
    transition: transform 0.6s ease;
  }
  .epsp-slider-track .epsp-slider-img { min-width: 100%; flex-shrink: 0; }
  .epsp-slider-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .epsp-slider-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
  }
  .epsp-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #B8B7B7;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s;
  }
  .epsp-dot.epsp-dot-active { background: #EB2019; }

  /* ── RIGHT PARAGRAPHS ── */
  .epsp-right-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-top: 10px;
  }
  .epsp-para {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }
  .epsp-para-bold {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #202020;
    line-height: 160%;
  }

  /* ══════════════════════════════════════════
     RED DIVIDER
  ══════════════════════════════════════════ */
  .epsp-divider {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }
  .epsp-divider hr {
    border: none;
    border-top: 1px solid #EB2019;
    opacity: 0.3;
  }

  /* ══════════════════════════════════════════
     4. TRANSFORMER SECTION
  ══════════════════════════════════════════ */
  .epsp-transformer-section {
    padding: 80px 0 0 0;
  }
  .epsp-tr-red-title {
    font-family: 'Poppins', sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: #EB2019;
    margin-bottom: 24px;
    line-height: 160%;
  }
  .epsp-tr-paras {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 80px;
  }

  /* ══════════════════════════════════════════
     5. PRODUCT CARDS — 1-Phase / 3-Phase
  ══════════════════════════════════════════ */
  .epsp-cards-section {
    padding: 80px 0 120px 0;
  }
  .epsp-cards-grid {
    display: flex;
    gap: 30px;
  }
  .epsp-card {
    flex: 1;
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    height: 350px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-decoration: none;
  }
  .epsp-card-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  /* Dark red overlay */
  .epsp-card-overlay {
    position: absolute;
    inset: 0;
    
  }
  .epsp-card-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
  .epsp-card-title {
    font-family: 'Poppins', sans-serif;
    font-size: 42px;
    font-weight: 600;
    color: #fff;
    text-align: center;
    line-height: 120%;
  }
  .epsp-card-btn {
    display: inline-block;
    padding: 14px 36px;
   
    border-radius: 6px;
    background: #fff;
    color: #202020;
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }
  .epsp-card-btn:hover {
    background: #fff;
    color: #EB2019;
  }

  /* ══════════════════════════════════════════
     RESPONSIVE — TABLET 768px
  ══════════════════════════════════════════ */
  @media (max-width: 768px) {
    .epsp-hero              { height: 300px; }
    .epsp-hero-bg           { display: block; }
    .epsp-hero-bg-mobile    { display: none; }
    .epsp-hero-content      { padding: 0 32px; }
    .epsp-hero-title        { font-size: 28px; }
    .epsp-hero-divider      { width: 260px; }
    .epsp-hero-desc         { font-size: 14px; }

    .epsp-breadcrumb        { padding: 28px 0 0 0; }
    .epsp-breadcrumb-inner  { padding: 0 32px; }
    .epsp-inner             { padding: 0 32px; }
    .epsp-divider           { padding: 0 32px; }

    .epsp-section-content   { padding: 40px 0 0 0; }
    .epsp-red-title         { font-size: 24px; margin-bottom: 30px; }

    .epsp-top-grid          { flex-direction: column; gap: 30px; margin-bottom: 40px; }
    .epsp-slider            { flex: none; width: 100%; }
    .epsp-slider-img-wrap   { height: 280px; }

    .epsp-para              { font-size: 14px; }
    .epsp-para-bold         { font-size: 14px; }

    .epsp-transformer-section { padding: 40px 0 0 0; }
    .epsp-tr-red-title      { font-size: 18px; }
    .epsp-tr-paras          { margin-bottom: 40px; gap: 16px; }

    .epsp-cards-section     { padding: 40px 0 60px 0; }
    .epsp-cards-grid        { gap: 16px; }
    .epsp-card              { height: 240px; }
    .epsp-card-title        { font-size: 22px; }
    .epsp-card-btn          { padding: 10px 24px; font-size: 14px; }
  }

  /* ══════════════════════════════════════════
     RESPONSIVE — MOBILE 480px
  ══════════════════════════════════════════ */
  @media (max-width: 480px) {
    .epsp-hero              { height: 400px; }
    .epsp-hero-bg           { display: none; }
    .epsp-hero-bg-mobile    { display: block; }
    .epsp-hero-content      { padding: 0 16px; }
    .epsp-hero-title        { font-size: 24px; }
    .epsp-hero-divider      { width: 100%; margin-bottom: 15px; }
    .epsp-hero-desc         { font-size: 14px; }

    .epsp-breadcrumb        { padding: 22px 0 0 0; }
    .epsp-breadcrumb-inner  { padding: 0 16px; gap: 16px; }
    .epsp-bc-item           { font-size: 14px; }
    .epsp-bc-sep            { width: 18px; height: 18px; margin: 0 4px; }
    .epsp-inner             { padding: 0 16px; }
    .epsp-divider           { padding: 0 16px; }

    .epsp-section-content   { padding: 30px 0 0 0; }
    .epsp-red-title         { font-size: 20px; margin-bottom: 20px; }
    .epsp-slider-img-wrap   { height: 240px; }
    .epsp-dot               { width: 6px; height: 6px; }
    .epsp-top-grid { margin-bottom: 60px;}
    .epsp-right-text { padding-top: 0px;}

    .epsp-transformer-section { padding: 60px 0 0 0; }
    .epsp-tr-red-title      { font-size: 16px; }
    .epsp-tr-paras          { margin-bottom: 60px; gap: 14px; }

    .epsp-cards-section     { padding: 60px 0 60px 0; }
    .epsp-cards-grid        { flex-direction: column; gap: 16px; height: 350px !important; }
    .epsp-card              { height: 350px !important; }
    .epsp-card-content      { gap: 30px; }
    .epsp-card-title        { font-size: 20px; }
    .epsp-card-btn          { padding: 11.5px 36px; font-size: 14px; }
  }
`;

export default function EspPowerSupplies() {
  const [activeSlide, setActiveSlide] = useState(0);
  const intervalRef = useRef(null);
  const dragStartX  = useRef(null);

  /* ── Autoplay ── */
  const stopAutoplay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  const startAutoplay = () => {
    stopAutoplay();
    intervalRef.current = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % SLIDES.length);
    }, AUTOPLAY_SPEED);
  };
  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, []);

  /* ── Dot click ── */
  const goToSlide = (i) => {
    stopAutoplay();
    setActiveSlide(i);
    startAutoplay();
  };

  /* ── Drag / swipe ── */
  const onDragStart = (e) => {
    dragStartX.current = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    stopAutoplay();
  };
  const onDragEnd = (e) => {
    if (dragStartX.current === null) return;
    const endX = e.type === "touchend" ? e.changedTouches[0].clientX : e.clientX;
    const diff  = dragStartX.current - endX;
    if (Math.abs(diff) > 80) {
      setActiveSlide(prev =>
        diff > 0
          ? (prev + 1) % SLIDES.length
          : (prev - 1 + SLIDES.length) % SLIDES.length
      );
    }
    dragStartX.current = null;
    startAutoplay();
  };

  return (
    <div className="epsp-wrap">
      <style>{CSS}</style>

      {/* ══ 1. HERO ══ */}
      <section className="epsp-hero">
        <img className="epsp-hero-bg"        src={HeroBgImg}       alt="" />
        <img className="epsp-hero-bg-mobile" src={HeroBgImgMobile} alt="" />
        <div className="epsp-hero-overlay" />
        <div className="epsp-hero-content">
          <h1 className="epsp-hero-title">ESP Power Supplies</h1>
          <hr className="epsp-hero-divider" />
          <p className="epsp-hero-desc">
            The function of Electrostatic Precipitator (ESP) is to capture suspended particulate matter like dust, fly-ash, aerosols etc.
          </p>
        </div>
      </section>

      {/* ══ 2. BREADCRUMB ══ */}
      <nav className="epsp-breadcrumb" aria-label="Breadcrumb">
        <div className="epsp-breadcrumb-inner">
          <a href="/" className="epsp-bc-item">Home</a>
          <span className="epsp-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <a href="/products" className="epsp-bc-item">Products</a>
          <span className="epsp-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <span className="epsp-bc-item epsp-active">ESP Power Supplies</span>
        </div>
      </nav>

      {/* ══ 3. CONTENT ══ */}
      <section className="epsp-section-content">
        <div className="epsp-inner">

          {/* Red page title */}
          <h2 className="epsp-red-title">ESP Power Supplies</h2>

          {/* Two-col: slider left + paragraphs right */}
          <div className="epsp-top-grid">

            {/* Left — slider */}
            <div className="epsp-slider">
              <div
                className="epsp-slider-img-wrap"
                onMouseDown={onDragStart}
                onMouseUp={onDragEnd}
                onMouseLeave={() => { dragStartX.current = null; startAutoplay(); }}
                onTouchStart={onDragStart}
                onTouchEnd={onDragEnd}
              >
                <div
                  className="epsp-slider-track"
                  style={{ transform: `translateX(-${activeSlide * 100}%)` }}
                >
                  {SLIDES.map((src, i) => (
                    <img
                      key={i}
                      className="epsp-slider-img"
                      src={src}
                      alt={`Slide ${i + 1}`}
                      draggable={false}
                    />
                  ))}
                </div>
              </div>
              <div className="epsp-slider-dots">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    className={`epsp-dot${activeSlide === i ? " epsp-dot-active" : ""}`}
                    onClick={() => goToSlide(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Right — two paragraphs */}
            <div className="epsp-right-text">
              <p className="epsp-para">
                The function of Electrostatic Precipitator (ESP) is to capture suspended particulate matter like dust, fly-ash, aerosols etc. from exhaust gases, typically from that of an industrial Boiler by creating a strong electrostatic field. This electrostatic field in the ESP is the output of a high-voltage power source referred to as 'ESP power supply' which basically converts the power from AC mains (power source) to a controlled High-voltage DC current. The low-voltage AC input to High-voltage DC output is achieved by a Transformer-Rectifier unit whereas the output power is optimized for the operating conditions of the project application (load) by use of a Control cubicle.
              </p>
              <p className="epsp-para">
                Ador is the global leader in manufacturing of the complete Power supply unit.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="epsp-divider"><hr /></div>

      {/* ══ 4. TRANSFORMER SECTION ══ */}
      <section className="epsp-transformer-section">
        <div className="epsp-inner">

          <h2 className="epsp-tr-red-title">6.0 - High Voltage Transformer/Rectifier sets</h2>

          <div className="epsp-tr-paras">
            <p className="epsp-para">
              High Voltage Transformer Rectifier units are available in single-phase and three-phase versions. Essentially, the two systems differ in the performance of the electrostatic precipitator in terms of dust separation.
            </p>
            <p className="epsp-para">
              We have the capability to customize the TR unit to meet the project-specific requirement of input voltage ranges (380V to 690V) and input frequencies (50Hz, 60Hz). Similarly, we offer a very wide voltage range on the output side viz. from 30 kVp to 200 kVp and output current ranging from 50 mA to 5000 mA.
            </p>
            <p className="epsp-para">
              Customized TR sets to meet any other input and output values can also be met using Ador's rich experience.
            </p>
            <p className="epsp-para-bold">
              Furthermore, we can adapt the main dimensions to meet any project-specific installation requirement of the electrostatic precipitator.
            </p>
          </div>

        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="epsp-divider"><hr /></div>

      {/* ══ 5. PRODUCT CARDS ══ */}
      <section className="epsp-cards-section">
        <div className="epsp-inner">
          <div className="epsp-cards-grid">

            {/* 1-Phase Solutions card */}
            <a href="/products/esp-power-supplies/1-phase" className="epsp-card">
              <img className="epsp-card-bg" src={PhaseOneImg} alt="" />
              <div className="epsp-card-overlay" />
              <div className="epsp-card-content">
                <h3 className="epsp-card-title">1-Phase Solutions</h3>
                <span className="epsp-card-btn">Read More</span>
              </div>
            </a>

            {/* 3-Phase Solutions card */}
            <a href="/products/esp-power-supplies/3-phase" className="epsp-card">
              <img className="epsp-card-bg" src={PhaseThreeImg} alt="" />
              <div className="epsp-card-overlay" />
              <div className="epsp-card-content">
                <h3 className="epsp-card-title">3-Phase Solutions</h3>
                <span className="epsp-card-btn">Read More</span>
              </div>
            </a>

          </div>
        </div>
      </section>

    </div>
  );
}